/**
 * En qué punto del recorrido está una atención.
 *
 * POR QUÉ UN COMPONENTE Y NO UN BADGE EN CADA PANTALLA
 *
 * "Programada" es el estado de la CITA, no del paciente: dice que existe una
 * hora reservada, no si ya pagó ni si pasó por triaje. Para saberlo había que
 * abrir la atención una por una.
 *
 * El estado lo calcula el servidor —del pago, del triaje y del episodio reales—
 * y todas las pantallas muestran la misma palabra. Que la lista, la bandeja y
 * la ficha coincidan no es cosmética: si cada una lo nombra distinto, dos
 * personas hablando por teléfono no se entienden.
 *
 * El vocabulario es el de las bandejas de Mi Espacio: por cobrar → pasa a
 * triaje → listo para atender → en consulta → atendida.
 */
import { Badge } from "../ui";

type Variante = "neutral" | "success" | "warning" | "danger";

const ESTILO: Record<string, Variante> = {
  CANCELADA: "danger",
  NO_ASISTIO: "danger",
  POR_COBRAR: "warning",
  PASA_TRIAJE: "warning",
  LISTA_ATENDER: "success",
  EN_CONSULTA: "success",
  ATENDIDA: "neutral",
};

// Los cinco pasos del flujo, los mismos que la guía de la ficha del paciente.
const PASOS = ["Cita", "Pago", "Triaje", "Consulta", "Receta"];

export function EstadoFlujo({
  estado,
  label,
  paso,
  conPasos = false,
}: {
  estado: string;
  label: string;
  /** 0 = fuera del flujo (cancelada). 2..5 = paso en el que está. */
  paso: number;
  /** Añade el "2 de 5" para las pantallas donde hay sitio. */
  conPasos?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Badge variant={ESTILO[estado] ?? "neutral"}>{label}</Badge>
      {conPasos && paso > 0 && (
        <span className="font-mono text-[11px] text-slate-400" title={PASOS[paso - 1]}>
          {paso}/5
        </span>
      )}
    </span>
  );
}

/** Atajo para no repetir el desestructurado en cada lista. */
export function EstadoFlujoDeCita({
  cita,
  conPasos = false,
}: {
  cita: {
    estado_flujo: string;
    estado_flujo_label: string;
    estado_flujo_paso: number;
  };
  conPasos?: boolean;
}) {
  return (
    <EstadoFlujo
      estado={cita.estado_flujo}
      label={cita.estado_flujo_label}
      paso={cita.estado_flujo_paso}
      conPasos={conPasos}
    />
  );
}
